/* Félivao — progressive enhancements. Everything works and stays visible without this file. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Reveal on scroll: only elements still below the fold are hidden, so nothing
     visible at load time ever flashes or disappears. */
  const REVEAL_SELECTOR = [
    '.fv-section-head',
    '.fv-range-card',
    '.fv-finder',
    '.fv-steps li',
    '.fv-pillar',
    '.fv-manifesto__statement',
    '.fv-compare__scroller',
    '.fv-journal-card',
    '.fv-accordion',
    '.fv-final__card',
    '.fv-guide-card',
    '.fv-pdp-panel',
  ].join(',');

  const initReveal = (root = document) => {
    if (reduceMotion || !('IntersectionObserver' in window)) return;
    const viewportHeight = window.innerHeight;
    const items = [...root.querySelectorAll(REVEAL_SELECTOR)].filter(
      (el) => !el.hasAttribute('data-fv-reveal') && el.getBoundingClientRect().top > viewportHeight * 0.92
    );
    if (!items.length) return;

    document.documentElement.classList.add('fv-motion');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.08 }
    );

    items.forEach((el) => {
      const siblings = [...(el.parentElement?.children || [])].filter((node) => node.matches(REVEAL_SELECTOR));
      el.style.setProperty('--fv-reveal-i', String(Math.min(siblings.indexOf(el), 4)));
      el.setAttribute('data-fv-reveal', '');
      observer.observe(el);
    });

    /* Safety net: never leave content hidden (e.g. print, unusual scroll containers). */
    window.setTimeout(() => items.forEach((el) => el.classList.add('is-revealed')), 6000);
  };

  /* Format finder */
  const WHY = {
    compact: {
      veto: 'Compact et léger, il se porte à la main ou en bandoulière : pensé pour les rendez-vous et les trajets courts.',
    },
    large: {
      veto: 'Votre chat a besoin d’espace : le grand format existe en deux tailles, à choisir selon ses mesures.',
      car: 'Pour la voiture et les week-ends, le grand format offre plus de place, un tapis amovible et une sangle pour valise.',
    },
    backpack: {
      walk: 'Mains libres pour marcher, avec façade et côtés en maille. Vérifiez que votre chat tient à l’aise dans 32 × 28 × 40 cm.',
      walkLarge:
        'Pour marcher mains libres. Pour un grand chat, comparez bien ses mesures aux 32 × 28 × 40 cm extérieurs : le grand format reste plus spacieux.',
    },
  };

  const recommend = (trip, size) => {
    if (trip === 'walk') return { key: 'backpack', why: size === 'large' ? WHY.backpack.walkLarge : WHY.backpack.walk };
    if (trip === 'car') return { key: 'large', why: WHY.large.car };
    if (size === 'large') return { key: 'large', why: WHY.large.veto };
    return { key: 'compact', why: WHY.compact.veto };
  };

  const initFinder = (root = document) => {
    root.querySelectorAll('[data-fv-finder]').forEach((finder) => {
      if (finder.dataset.fvReady) return;
      finder.dataset.fvReady = 'true';

      const update = () => {
        const trip = finder.querySelector('input[name^="fv-trip"]:checked')?.value;
        const size = finder.querySelector('input[name^="fv-size"]:checked')?.value;
        if (!trip || !size) return;

        const { key, why } = recommend(trip, size);
        const target = finder.querySelector(`[data-fv-pick="${key}"]`);
        if (!target) return;

        finder.querySelector('[data-fv-empty]')?.setAttribute('hidden', '');
        finder.querySelectorAll('[data-fv-pick]').forEach((pick) => {
          pick.hidden = pick !== target;
        });
        const whyNode = target.querySelector('[data-fv-why]');
        if (whyNode) whyNode.textContent = why;
      };

      finder.addEventListener('change', update);
    });
  };

  /* Background videos: load and play only when visible. */
  const initLazyVideo = (root = document) => {
    const videos = root.querySelectorAll('[data-fv-lazy-video] video');
    if (!videos.length || reduceMotion || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    });
    videos.forEach((video) => observer.observe(video));
  };

  const init = (root) => {
    initFinder(root);
    initReveal(root);
    initLazyVideo(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
